import { useState } from 'react'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatApiErrorMessage, getApiErrorMessage } from '@/lib/api-error'
import { cn, formatMoney } from '@/lib/utils'
import { arrivalLabel, carrierTitle, serviceLabel } from './carrier-options-model'
import {
  useBookCarrierOption,
  useShipmentCarrierOptions,
  type CarrierOption,
} from '@/features/purchase-orders/hooks'

const SHOWN = 6

function optionKey(option: CarrierOption) {
  return [option.scac_code ?? option.carrier_name, option.service_level, option.provider_service_code ?? ''].join('|')
}

/**
 * Every carrier that will take this shipment right now, cheapest first: price, transit and the
 * delivery date, each bookable in one click (USA-1738). Asked live, so prices match booking.
 */
export function ShipmentCarrierOptions({
  poUuid,
  shipmentId,
  onBooked,
  className = 'px-3 pb-1',
}: {
  poUuid: string
  shipmentId: number
  onBooked?: () => void
  className?: string
}) {
  const options = useShipmentCarrierOptions(poUuid, shipmentId, true)
  const book = useBookCarrierOption(poUuid)
  const bookingKey = book.isPending && book.variables ? optionKey(book.variables.option) : null
  const [showAll, setShowAll] = useState(false)
  const data = options.data
  const all = data?.options ?? []
  // The cheapest few, and the fastest even when it costs more.
  const shown = showAll ? all : all.filter((option, index) => index < SHOWN || option.fastest)

  let body
  if (options.isLoading) {
    body = (
      <div className="space-y-2 py-2" role="status" aria-label="Asking carriers">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
      </div>
    )
  } else if (options.isError) {
    body = (
      <p className="py-2 text-sm text-red-700 dark:text-red-300">
        {getApiErrorMessage(options.error) || 'No prices from the carriers right now.'}
      </p>
    )
  } else if (data?.missing.length) {
    body = <p className="py-2 text-sm text-muted-foreground">Add {data.missing.join(', ')} to compare carriers.</p>
  } else if (data?.unavailable) {
    body = <p className="py-2 text-sm text-muted-foreground">{data.unavailable}</p>
  } else if (!all.length) {
    body = <p className="py-2 text-sm text-muted-foreground">No carrier offered a price.</p>
  } else {
    body = (
      <>
      <ul className="divide-y" aria-label="Carrier options">
        {shown.map((option) => {
          const key = optionKey(option)
          return (
            <li
              key={key}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-2 sm:grid-cols-[minmax(0,1fr)_11rem_5.5rem_auto]"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                  <span className="break-words">{carrierTitle(option)}</span>
                  {option.cheapest && <StatusPill tone="success">Cheapest</StatusPill>}
                  {option.fastest && <StatusPill tone="progress">Fastest</StatusPill>}
                </p>
                {serviceLabel(option) && (
                  <p className="truncate text-xs text-muted-foreground">{serviceLabel(option)}</p>
                )}
              </div>
              <p className="whitespace-nowrap text-sm tabular-nums max-sm:col-start-1 max-sm:row-start-2">
                {option.estimated_delivery_date ? arrivalLabel(option.estimated_delivery_date) : 'No date'}
                {option.transit_days != null && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {option.transit_days} {option.transit_days === 1 ? 'day' : 'days'}
                  </span>
                )}
              </p>
              <p className="text-right text-sm font-semibold tabular-nums max-sm:col-start-2 max-sm:row-start-1">
                {formatMoney(option.total_price)}
              </p>
              <LoadingButton
                size="sm"
                variant="outline"
                className="h-7 justify-self-end text-xs max-sm:col-start-2 max-sm:row-start-2"
                loading={bookingKey === key}
                loadingText="Booking..."
                disabled={book.isPending}
                aria-label={`Book ${carrierTitle(option)} ${serviceLabel(option) ?? ''} for ${formatMoney(option.total_price)}`}
                onClick={() =>
                  book.mutate(
                    { shipmentId, option },
                    {
                      onSuccess: () => {
                        toast.success(`Booking ${carrierTitle(option)} — the label or BOL goes to the supplier once it confirms`)
                        onBooked?.()
                      },
                      onError: (err) => toast.error(formatApiErrorMessage('Not booked', err)),
                    },
                  )
                }
              >
                Book
              </LoadingButton>
            </li>
          )
        })}
      </ul>
      {all.length > shown.length || showAll ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto px-0 pb-1 text-xs text-muted-foreground"
          onClick={() => setShowAll((open) => !open)}
        >
          {showAll ? 'Show fewer' : `Show all ${all.length}`}
        </Button>
      ) : null}
      </>
    )
  }

  return (
    <section aria-label="Compare carriers" className={className}>
      <div className="flex items-center justify-between gap-2 pt-2">
        <h4 className="text-xs font-medium text-muted-foreground">Carriers</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          disabled={options.isFetching}
          onClick={() => options.refetch()}
        >
          <RefreshCw className={cn('mr-1 size-3', options.isFetching && 'animate-spin')} aria-hidden />
          Refresh
        </Button>
      </div>
      {body}
    </section>
  )
}
